// src/components/dashboard/candidate/journey-steps/CelebrationModal.tsx
//
// The last thing a new candidate sees. Three things were wrong with it, and all
// three were the same mistake — the view described a product rather than
// reporting what had just happened:
//
//  1. The panel was bg-[#A4C3B2]/30 over a black/60 scrim, so dark-teal
//     headings sat on a dark, half-transparent ground and could not be read.
//  2. "Du är nu matchad med 12 möjligheter" — 12 was a hard-coded default prop
//     that nobody ever passed. A fabricated number, shown as fact, to a real
//     person on their first screen.
//  3. "Omdirigerar till din dashboard..." — it navigated to the landing page,
//     and the account is not signed in at that point either.
//
// What it says now is only what is true: the account exists, the profile is
// saved, and here is the one next step.
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Mail, LogIn } from 'lucide-react';

interface CelebrationModalProps {
    isOpen: boolean;
    /**
     * 'signup' — account AND profile were just created, nobody is signed in.
     * 'profile' — an already signed-in candidate just filled in their profile.
     * They need different words: "Ditt konto är skapat / Logga in" is wrong for
     * someone who is already logged in and never created an account here.
     */
    variant?: 'signup' | 'profile';
    /**
     * Whether the account is already usable. Comes from the signup response
     * (`is_verified`), because it depends on backend config: with auto-verify on
     * no confirmation mail is sent at all, and telling someone to go and find
     * one would send them looking for a mail that was never sent.
     */
    isVerified?: boolean;
    /** The address we sent the confirmation to, shown so a typo is catchable. */
    email?: string;
    /** Continue — dismisses and moves the user on. */
    onContinue?: () => void;
}

export const CelebrationModal: React.FC<CelebrationModalProps> = ({
    isOpen,
    variant = 'signup',
    isVerified = false,
    email,
    onContinue,
}) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setShow(false);
            return;
        }
        const t = setTimeout(() => setShow(true), 60);
        return () => clearTimeout(t);
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div
                className={`relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 ${
                    show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                }`}
            >
                {/* Header band — the sage is decoration here, not the text ground. */}
                <div className="bg-[#1A4D4F] px-8 pt-8 pb-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-8 h-8 text-[#1A4D4F]" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#f5f5f5]">
                        {variant === 'profile' ? 'Din profil är klar!' : 'Ditt konto är skapat!'}
                    </h2>
                    <p className="text-sm text-[#f5f5f5]/80 mt-1">
                        {variant === 'profile'
                            ? 'Dina svar är sparade.'
                            : 'Din profil är sparad och klar.'}
                    </p>
                </div>

                <div className="px-8 py-6">
                    {variant === 'profile' ? (
                        <div className="flex items-start gap-3 mb-6">
                            <div className="w-9 h-9 bg-[#A4C3B2] rounded-full flex items-center justify-center flex-shrink-0">
                                <CheckCircle2 className="w-4 h-4 text-[#1A4D4F]" />
                            </div>
                            <p className="text-sm text-gray-700 pt-1.5">
                                Du kan uppdatera dina uppgifter när som helst under Min profil.
                            </p>
                        </div>
                    ) : isVerified ? (
                        <div className="flex items-start gap-3 mb-6">
                            <div className="w-9 h-9 bg-[#A4C3B2] rounded-full flex items-center justify-center flex-shrink-0">
                                <LogIn className="w-4 h-4 text-[#1A4D4F]" />
                            </div>
                            <p className="text-sm text-gray-700 pt-1.5">
                                Logga in med din e-postadress och ditt lösenord för att komma igång.
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-start gap-3 mb-6">
                            <div className="w-9 h-9 bg-[#A4C3B2] rounded-full flex items-center justify-center flex-shrink-0">
                                <Mail className="w-4 h-4 text-[#1A4D4F]" />
                            </div>
                            <div className="text-sm text-gray-700 pt-1">
                                <p>
                                    Vi har skickat en bekräftelselänk
                                    {email ? <> till <span className="font-semibold break-all">{email}</span></> : null}.
                                    Klicka på länken för att bekräfta din adress.
                                </p>
                                {/* Was "Du kan logga in direkt" — true until
                                    APP_REQUIRE_VERIFIED_LOGIN (base v1.66.0)
                                    started refusing sign-in for unconfirmed
                                    accounts. Every Nexilink env file sets it
                                    true, so confirming is now a step, not a
                                    courtesy. */}
                                <p className="text-gray-500 mt-1">
                                    Du behöver bekräfta adressen innan du kan logga in.
                                </p>
                            </div>
                        </div>
                    )}

                    {onContinue && (
                        <button
                            type="button"
                            onClick={onContinue}
                            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
                        >
                            {variant === 'profile' ? (
                                'Fortsätt'
                            ) : (
                                <>
                                    <LogIn className="w-4 h-4" />
                                    Logga in
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
